import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EVENT_ICONS, getEventIconUrl as getEventIconUrlFallback } from '@/constants/eventIcons';

interface EventIconRecord {
  event_type: string;
  icon_url: string;
  category: 'pricing' | 'packaging' | 'product';
}

/**
 * Hook to fetch event type icons from the database
 * Falls back to hardcoded constants if database is unavailable
 */
export const useEventIcons = () => {
  return useQuery({
    queryKey: ['event-icons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_event_type_icons')
        .select('event_type, icon_url, category');

      if (error) {
        console.warn('[useEventIcons] Failed to fetch from database, using fallback:', error);
        // Return fallback data from constants
        return Object.entries(EVENT_ICONS).flatMap(([category, icons]) =>
          Object.entries(icons).map(([event_type, icon_url]) => ({
            event_type,
            icon_url,
            category: category as 'pricing' | 'packaging' | 'product',
          }))
        );
      }

      return data as EventIconRecord[];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (icons rarely change)
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });
};

/**
 * Get icon URL for a specific event type
 * Uses database data if available, falls back to constants
 */
export const getEventIconUrl = (eventType: string, iconsData?: EventIconRecord[]): string | undefined => {
  if (iconsData && iconsData.length > 0) {
    const icon = iconsData.find(i => i.event_type === eventType);
    return icon?.icon_url;
  }

  // Fallback to constants
  return getEventIconUrlFallback(eventType);
};
